/**
 * Per-category estimation form configuration.
 * Encodes the exact enums, cascading dropdown logic, contractor handling and
 * row payload shape for each module — ported verbatim from the mobile screens
 * (structure.tsx, electrical.tsx, mechanic.tsx, finishing.tsx) so behaviour is identical.
 */

export const UNIT_OPTIONS = ["m", "m²", "m³", "Piece", "Km", "Cm"];
export const RETENTION_OPTIONS = [0, 5, 10, 15, 20];
export const HOME_TYPES = ["A", "B", "C"];

const buildLabelMap = (keys, en, ku) => {
  const map = {};
  keys.forEach((k, i) => {
    map[k] = { en: en[i], ku: ku[i] };
  });
  return map;
};

// ============ STRUCTURE ============
const S_MAIN_KEYS = [
  "دیواری ڕگر", "هەیکەل", "لین کۆنکرێت", "شمشە و ماڵە چی", "سیاجی دیوار",
  "کاری بلۆککاری", "ڵینتڵ", "مەرەدی ناو دەرگا", "کاری معالەجە",
  "کۆنکرێتی حەوشەی ڤێلاکان", "شاپ",
];
const S_MAIN_EN = [
  "Retaining Wall", "Structure Frame", "Lean Concrete", "Formwork & Scaffolding",
  "Wall Fence", "Brick Work", "Lintel Work", "Parapet", "Treatment Work",
  "Yard Concrete for the Villas", "Screed",
];
const S_SUB_MAP = {
  "دیواری ڕگر": {
    keys: ["أساس (کۆنکرێت)", "بلۆککاری", "کۆنکرێتی تەواوی دیواری ڕگر"],
    en: ["Foundation (Concrete)", "Block Work", "Full Retaining Wall Concrete"],
  },
  "شمشە و ماڵە چی": {
    keys: ["Foundation", "Slab", "Roof", "حەوشە"],
    en: ["Foundation", "Slab", "Roof", "Yard"],
  },
  "سیاجی دیوار": {
    keys: ["سیاجی دیواری لا", "سیاجی دیواری پێشەوە"],
    en: ["Side Wall Fence", "Front Wall Fence"],
  },
  "کاری بلۆککاری": {
    keys: ["بلۆکی عێراقی ٢٠سم", "بلۆکی عێراقی ١٥سم", "کاری بلۆککاری لە نێوان ستوونەکان"],
    en: ["Iraqi Brick 20cm", "Iraqi Brick 15cm", "Brick Work between Pillars"],
  },
  "کاری معالەجە": {
    keys: ["معالەجەی ناوەوە", "معالەجەی دەرەوە", "معالەجەی دکت", "معالەجەی بۆری"],
    en: ["Interior Treatment", "Exterior Treatment", "Duct Treatment", "Pipe Treatment"],
  },
};
const S_SUB_SUB_MAP = {
  "کۆنکرێتی تەواوی دیواری ڕگر": { keys: ["لین", "دیوار"], en: ["Lean", "Wall"] },
};
const S_WORK_PLACE = ["بەشی سەرەوە", "بەشی خوارەوە"];

// ============ ELECTRICAL ============
const E_KEYS = ["دانانی بۆری", "راکێشانی وایەر", "دانانی سەرگەچ", "تێستکردن"];
const E_EN = ["Pipe Installation", "Wire Pulling", "Above Plaster Installation", "Testing"];

// ============ MECHANIC ============
const M_MAIN_KEYS = [
  "شۆفاژ", "درەین سپلێت", "کاری میکانیک سەر گەچ", "تێست کردنی بۆری ئاو و ئاوەڕۆ",
  "بۆری هەوایی", "دەکت", "ئاوەڕۆ", "ئاوەڕۆی ئەساس", "ئاو", "سپلێت", "VRF system",
];
const M_MAIN_EN = [
  "Heating / Radiator", "Drain Split", "Mechanic Work on Plaster",
  "Water & Drainage Pipe Testing", "Air Pipe", "Duct", "Drainage",
  "Foundation Drainage", "Water", "Split", "VRF System",
];
const M_SPLIT = {
  keys: ["cassette type", "wall mounted", "installation fee", "pipe"],
  en: ["cassette type", "wall mounted", "installation fee", "pipe"],
};
const M_WATER = {
  keys: ["ئاو/قۆناغی ١", "ئاو/قۆناغی ٢"],
  en: ["Water / Phase 1", "Water / Phase 2"],
};
const M_DRAINAGE = {
  keys: [
    "ئاوەڕۆی ئەرزی", "ئاوەڕۆی سەقفی/ قۆناغی ١", "ئاوەڕۆی سەقفی/ قۆناغی ٢",
    "ئاوەڕۆی دەرەوەی ڤێلا/قۆناغی ١", "ئاوەڕۆی دەرەوەی ڤێلا/قۆناغی ٢",
  ],
  en: [
    "Ground Drainage", "Ceiling Drainage / Phase 1", "Ceiling Drainage / Phase 2",
    "External Villa Drainage / Phase 1", "External Villa Drainage / Phase 2",
  ],
};

// ============ FINISHING ============
const F_CAT_KEYS = ["کاری دەرەوەی ڤێلا", "کاری ناوەوی ڤێلا"];
const F_CAT_EN = ["Exterior Villa Work", "Interior Villa Work"];
const F_WORK_MAP = {
  "کاری دەرەوەی ڤێلا": {
    keys: ["لەبغی دەرەوە", "پۆرسەلین", "کاری ئاسنگەری", "بۆیاخ", "واجیهەی تەختە و ئەلەمنیۆم", "سەوزای", "کەپر(Shading)", "Labeling", "Exterior Tiles"],
    en: ["Exterior Plaster", "Porcelain", "Ironwork", "Paint", "Board & Aluminum Facade", "Landscaping", "Shading", "Labeling", "Tiles"],
  },
  "کاری ناوەوی ڤێلا": {
    keys: ["لەبغی بەشی خوارەوەی گەچ", "ئەلەمنیۆم", "شوشە", "سقف سانەوی", "لامینەیت ئەرزی", "ازاره", "دەرگا", "بۆیاخ", "Final Touch", "لەبغی تورکی", "گەچکاری", "مەڕمەڕ", "Interior Tiles"],
    en: ["Plaster Under Gypsum", "Aluminum", "Glass", "False Ceiling", "Floor Laminate", "Skirting", "Door", "Paint", "Final Touch", "Turkish Plaster", "Gypsum Work", "Marble", "Tiles"],
  },
};
const F_SUB_MAP = {
  "لەبغی دەرەوە": { keys: ["تێرمۆسیڤا", "لەبغی تورکی", "ژێر سەقف", "سیاجی دەرەوە"], en: ["Thermosiva", "Turkish Plaster", "Under Ceiling", "Exterior Fence"] },
  "کاری ئاسنگەری": { keys: ["سیاج و دەرگا", "قادرمە"], en: ["Fence & Door", "Railing"] },
  "بۆیاخ": { keys: ["بۆیاخی بینا", "سیاج و دەرگا"], en: ["Building Paint", "Fence & Door"] },
  "ئەلەمنیۆم": { keys: ["دەرگا و پەنجەرە", "باڵکۆنی", "قادرمە", "شاوەر"], en: ["Door & Window", "Balcony", "Railing", "Shower"] },
  "شوشە": { keys: ["دەرگا و پەنجەرە", "قادرمە", "باڵکۆنی", "شاوەر"], en: ["Door & Window", "Railing", "Balcony", "Shower"] },
  "سقف سانەوی": { keys: ["کناف", "بۆردێکس", "سپیکاری", "Z پرۆفایل", "20x20 فەتحەی سیانەی", "30x30 فەتحەی سیانەی", "40x40 فەتحەی سیانەی"], en: ["Knauf", "Bordex", "White Coating", "Z Profile", "20x20 Triple Opening", "30x30 Triple Opening", "40x40 Triple Opening"] },
  "لامینەیت ئەرزی": { keys: ["تەختە(سروشتی)", "تەختە (پلاستیک)"], en: ["Board (Natural)", "Board (Plastic)"] },
  "ازاره": { keys: ["پلاستیک", "ئەلەمنیۆم"], en: ["Plastic", "Aluminum"] },
  "دەرگا": { keys: ["مدخەل", "ناوەوە"], en: ["Entrance", "Interior"] },
  "Final Touch": { keys: ["دارتاشی", "پۆرسەلین"], en: ["Carpentry", "Porcelain"] },
  "گەچکاری": { keys: ["گڵگەچ", "سپیکاری"], en: ["Gypsum Plaster", "White Coating"] },
  "مەڕمەڕ": { keys: ["دەرگا", "پەنجەرە", "پلە قادرمە", "حمام تورکی", "شاوەر"], en: ["Door", "Window", "Stair Railing", "Turkish Bath", "Shower"] },
  // Item 1: new Tiles work types (sub-work types shown literally in Excel Work Type)
  "Interior Tiles": { keys: ["Wall", "Floor", "Skirting"], en: ["Wall", "Floor", "Skirting"] },
  "Exterior Tiles": { keys: ["Balcony", "The Courtyard", "Skirting"], en: ["Balcony", "The Courtyard", "Skirting"] },
};
// "کاشی" is the Kurdish display name for both Interior/Exterior Tiles work types
// (the internal key stays English/distinct so each routes to its own Excel template).
const FIN_TILES_DISPLAY = { "Interior Tiles": "کاشی", "Exterior Tiles": "کاشی" };
// Kurdish display labels for the Tiles sub-work types (English values stay internal).
const FIN_TILES_SUB_KU = { "Wall": "دیوار", "Floor": "ئەرزی", "Skirting": "ئیزارە", "Balcony": "باڵکۆنی", "The Courtyard": "حەوشە" };
const F_SUB_SUB_MAP = {};

// Build a flat label map (key -> {en, ku}) covering every dropdown value per category.
const mergeMaps = (...maps) => Object.assign({}, ...maps);
const fromCascade = (cascade) => {
  const out = {};
  Object.values(cascade).forEach((m) => {
    m.keys.forEach((k, i) => (out[k] = { en: m.en[i], ku: m.keys[i] }));
  });
  return out;
};

const STRUCTURE_LABELS = mergeMaps(
  buildLabelMap(S_MAIN_KEYS, S_MAIN_EN, S_MAIN_KEYS),
  fromCascade(S_SUB_MAP),
  fromCascade(S_SUB_SUB_MAP),
  // Display-only Kurdish override (stored key unchanged).
  { "کاری بلۆککاری لە نێوان ستوونەکان": { en: "Brick Work between Pillars", ku: "کاری خشتکاری لە نێوان ستوونەکان" } }
);
const ELECTRICAL_LABELS = buildLabelMap(E_KEYS, E_EN, E_KEYS);
const MECHANIC_LABELS = mergeMaps(
  buildLabelMap(M_MAIN_KEYS, M_MAIN_EN, M_MAIN_KEYS),
  fromCascade({ w: M_WATER, d: M_DRAINAGE, s: M_SPLIT })
);
const FINISHING_LABELS = mergeMaps(
  buildLabelMap(F_CAT_KEYS, F_CAT_EN, F_CAT_KEYS),
  fromCascade(F_WORK_MAP),
  fromCascade(F_SUB_MAP),
  fromCascade(F_SUB_SUB_MAP),
  {
    "Interior Tiles": { en: "Tiles", ku: "کاشی" },
    "Exterior Tiles": { en: "Tiles", ku: "کاشی" },
    "Wall": { en: "Wall", ku: "دیوار" },
    "Floor": { en: "Floor", ku: "ئەرزی" },
    "Skirting": { en: "Skirting", ku: "ئیزارە" },
    "Balcony": { en: "Balcony", ku: "باڵکۆنی" },
    "The Courtyard": { en: "The Courtyard", ku: "حەوشە" },
  }
);

// Display-only Kurdish label overrides (key/stored value stays unchanged).
// Structure › Brick Work › sub-type: show "خشتکاری" while the key remains "بلۆککاری".
const KU_DISPLAY_OVERRIDE = {
  "کاری بلۆککاری لە نێوان ستوونەکان": "کاری خشتکاری لە نێوان ستوونەکان",
};

const opts = (keys, en, language) => ({
  keys,
  labels: language === "ku" ? keys.map((k) => KU_DISPLAY_OVERRIDE[k] || k) : en,
});

const CONFIGS = {
  structure: {
    accent: "#2563EB",
    icon: "Building2",
    titleKey: "structure_estimation_form",
    categoryWorkKey: "structure_work",
    contractorMode: "top_level",
    usesTemplateConfig: true,
    configEndpoint: "/structure/template-config",
    labels: STRUCTURE_LABELS,
    primaryField: "main_type",
    dropdowns: [
      {
        field: "main_type",
        labelKey: "structure_type",
        placeholderKey: "select_structure_type",
        options: (row, language) => opts(S_MAIN_KEYS, S_MAIN_EN, language),
        visible: () => true,
      },
      {
        field: "sub_type",
        labelKey: "sub_work_type",
        placeholderKey: "select_sub_type",
        options: (row, language) => {
          const m = S_SUB_MAP[row.main_type];
          return m ? opts(m.keys, m.en, language) : { keys: [], labels: [] };
        },
        visible: (row) => !!S_SUB_MAP[row.main_type],
      },
      {
        field: "sub_sub_type",
        labelKey: "sub_sub_work_type",
        placeholderKey: "select_sub_type",
        options: (row, language) => {
          const m = S_SUB_SUB_MAP[row.sub_type];
          return m ? opts(m.keys, m.en, language) : { keys: [], labels: [] };
        },
        visible: (row) => !!row.sub_type && !!S_SUB_SUB_MAP[row.sub_type],
      },
    ],
    workPlaceOptions: S_WORK_PLACE,
    buildRowExtra: (r) => ({
      contractor_name: r.contractor_name || "",
      main_type: r.main_type,
      sub_type: r.sub_type,
      sub_sub_type: r.sub_sub_type,
      work_place: r.work_place || "",
    }),
    rowFields: { contractor_name: "", main_type: "", sub_type: "", sub_sub_type: "", work_place: "" },
  },

  electrical: {
    accent: "#D97706",
    icon: "Zap",
    titleKey: "electrical_estimation_form",
    categoryWorkKey: "electrical_work",
    contractorMode: "top_level",
    usesTemplateConfig: false,
    labels: ELECTRICAL_LABELS,
    primaryField: "electrical_type",
    dropdowns: [
      {
        field: "electrical_type",
        labelKey: "electrical_type",
        placeholderKey: "select_electrical_type",
        options: (row, language) => opts(E_KEYS, E_EN, language),
        visible: () => true,
      },
    ],
    buildRowExtra: (r) => ({
      electrical_type: r.electrical_type,
      main_type: r.electrical_type,
    }),
    rowFields: { electrical_type: "" },
  },

  mechanic: {
    accent: "#059669",
    icon: "Wrench",
    titleKey: "mechanic_estimation_form",
    categoryWorkKey: "mechanic_work",
    contractorMode: "top_level",
    usesTemplateConfig: false,
    labels: MECHANIC_LABELS,
    primaryField: "main_type",
    dropdowns: [
      {
        field: "main_type",
        labelKey: "main_work_type",
        placeholderKey: "select_main_type",
        options: (row, language) => opts(M_MAIN_KEYS, M_MAIN_EN, language),
        visible: () => true,
      },
      {
        field: "sub_type",
        labelKey: "sub_work_type",
        placeholderKey: "select_sub_type",
        options: (row, language) => {
          if (row.main_type === "ئاو") return opts(M_WATER.keys, M_WATER.en, language);
          if (row.main_type === "ئاوەڕۆ") return opts(M_DRAINAGE.keys, M_DRAINAGE.en, language);
          if (row.main_type === "سپلێت") return opts(M_SPLIT.keys, M_SPLIT.en, language);
          return { keys: [], labels: [] };
        },
        visible: (row) => row.main_type === "ئاو" || row.main_type === "ئاوەڕۆ" || row.main_type === "سپلێت",
      },
    ],
    buildRowExtra: (r) => ({ main_type: r.main_type, sub_type: r.sub_type }),
    rowFields: { main_type: "", sub_type: "" },
  },

  finishing: {
    accent: "#7C3AED",
    icon: "Paintbrush",
    titleKey: "finishing_estimation_form",
    categoryWorkKey: "finishing_work",
    contractorMode: "top_level",
    usesTemplateConfig: true,
    configEndpoint: "/finishing/template-config",
    labels: FINISHING_LABELS,
    primaryField: "main_type",
    dropdowns: [
      {
        field: "category_type",
        labelKey: "facility_type",
        placeholderKey: "select_finishing_type",
        options: (row, language) => opts(F_CAT_KEYS, F_CAT_EN, language),
        visible: () => true,
      },
      {
        field: "main_type",
        labelKey: "finishing_type",
        placeholderKey: "select_finishing_type",
        options: (row, language) => {
          const m = F_WORK_MAP[row.category_type];
          if (!m) return { keys: [], labels: [] };
          const labels = language === "ku"
            ? m.keys.map((k) => FIN_TILES_DISPLAY[k] || k)
            : m.en;
          return { keys: m.keys, labels };
        },
        visible: (row) => !!F_WORK_MAP[row.category_type],
      },
      {
        field: "sub_type",
        labelKey: "sub_work_type",
        placeholderKey: "select_sub_type",
        options: (row, language) => {
          const m = F_SUB_MAP[row.main_type];
          if (!m) return { keys: [], labels: [] };
          const labels = language === "ku"
            ? m.keys.map((k) => FIN_TILES_SUB_KU[k] || k)
            : m.en;
          return { keys: m.keys, labels };
        },
        visible: (row) => !!F_SUB_MAP[row.main_type],
      },
      {
        field: "sub_sub_type",
        labelKey: "sub_sub_work_type",
        placeholderKey: "select_sub_sub_type",
        options: (row, language) => {
          const m = F_SUB_SUB_MAP[row.sub_type];
          return m ? opts(m.keys, m.en, language) : { keys: [], labels: [] };
        },
        visible: (row) => !!row.sub_type && !!F_SUB_SUB_MAP[row.sub_type],
      },
    ],
    buildRowExtra: (r) => ({
      contractor_name: r.contractor_name || "",
      category_type: r.category_type,
      main_type: r.main_type,
      sub_type: r.sub_type,
      sub_sub_type: r.sub_sub_type,
    }),
    rowFields: { contractor_name: "", category_type: "", main_type: "", sub_type: "", sub_sub_type: "" },
  },
};

export const getCategoryConfig = (category) => CONFIGS[category] || CONFIGS.structure;

// Label for a stored key in the current language (falls back to key).
export const labelOf = (config, key, language) => {
  if (!key) return "";
  const entry = config.labels[key];
  return entry ? entry[language] : key;
};
